import type { PaginatedRequestQuery, PaginatedResponseWithTotal } from '../types/routes/pagination';

/**
 * Represents the path to which a code snippet was detected.
 * In general, there is a "many-to-many" relationship between snippets and paths.
 */
export type SnippetPath = {
  type: 'directory' | 'file';
  name: string;
  path: string;
};

export type GetSnippetPathsParams = {
  /**
   * The path from which a single depth of files and / or directories will be returned.
   */
  path?: string;
};

export type SnippetPathsResponse = {
  paths: SnippetPath[];
};

/**
 * Snippets can be detected in three different ways:
 * 1. Snippet is a partial match to a third party file.
 * 2. File, according to ScanOSS, is a 100% match to a third party file.
 * Since we are collapsing all matches together this may be a less useful distinction,
 * as some matches might not be 100%.
 * 3. Dependency is a 1-1 match between all snippets and all third party files (like you
 * have a complete copy of it).
 *
 * The default "kind" for a snippet is "snippet". In other words, if we cannot determine
 * that a snippet is a file or a dependency, we will assume it is just a snippet (partial match).
 */
export type SnippetKind = 'snippet' | 'file' | 'dependency';

export type SnippetLicense = {
  signature: string;
  type: 'declared' | 'discovered';
  status: 'approved' | 'denied' | 'flagged' | 'unknown';
  issueUrl?: string;
};

export type SnippetIssueCounts = {
  approved: number;
  denied: number;
  flagged: number;
  unknown: number;
};

export type SnippetIssueTypes = 'licensing';

export type SnippetMatch = {
  path: string;
  matchPercentage: number;
};

/**
 * Represents the metadata and details for a detected snippet.
 * This data will be used to display snippet overviews in a table
 * and to show details in a drawer.
 */
export type Snippet = {
  id: string;
  kind: SnippetKind;
  matches: SnippetMatch[];
  licenses: SnippetLicense[];
  issueCounts: Record<SnippetIssueTypes, SnippetIssueCounts>;
  highestMatchPercentage: number;
  releaseDate: string;
  homeUrl?: string;
  codeUrl?: string;
};

export const confidenceFilterOptions = ['low', 'high'] as const;
export type ConfidenceFilterOption = typeof confidenceFilterOptions[number];

export const licenseStatusFilterOptions = ['approved', 'denied', 'flagged', 'unknown'] as const;
export type LicenseStatusFilterOption = typeof licenseStatusFilterOptions[number];

/**
 * Represents the filter params available for snippets.
 */
export type SnippetsFilterParams = {
  path?: string;
  search?: string;
  confidence?: ConfidenceFilterOption[];
  licenseStatus?: LicenseStatusFilterOption[];
};

/**
 * Represents the parameters that can be used when fetching snippets.
 */
export type GetSnippetsParams = SnippetsFilterParams & PaginatedRequestQuery;

/**
 * Represents the parameters that can be used when counting snippets.
 */
export type CountSnippetsParams = SnippetsFilterParams;

export type SnippetsResponse = PaginatedResponseWithTotal<Snippet>;

export type SnippetResponse = {
  snippet: Snippet;
};

export type SnippetCodeLine = {
  line: string;
  lineNumber: number;
  isHighlighted: boolean;
};

/**
 * Represents a match between reference code and a detected code snippet.
 * In general, one snippet can have multiple matches. This indicates that
 * the same snippet was found in multiple places within the user’s project.
 */
export type SnippetMatchDetails = SnippetMatch & {
  referenceCode: SnippetCodeLine[];
  detectedCode: SnippetCodeLine[];
};

export type SnippetMatchDetailsResponse = {
  matchDetails: SnippetMatchDetails;
};

/**
 * Represents the query params used on the frontend.
 */
export type SnippetsQueryParams = GetSnippetsParams & {
  /**
   * Determines if the drawer is open, and if so, the ID of the snippet.
   */
  selectedSnippetId?: string;
};
